import axios from 'axios';
import { apiWithAuth } from './api';

const API_URL = process.env.REACT_APP_API_URL;
if (!API_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required. Please set it in your environment variables.');
}

export const getEvents = async () => {
  const response = await axios.get(`${API_URL}/events`);
  return response.data;
};

export const getEventById = async (eventId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get(`/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching event:', error);
    throw error;
  }
};

export const getEventStats = async (eventId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get(`/events/${eventId}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching event stats:', error);
    throw error;
  }
};

export const createEvent = async (eventData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).post('/events/', eventData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const token = localStorage.getItem('token');
    
    // Convert eventData to FormData (backend expects multipart/form-data)
    const formData = new FormData();
    
    // Add all fields to FormData
    if (eventData.title !== undefined) formData.append('title', eventData.title);
    if (eventData.description !== undefined) formData.append('description', eventData.description);
    if (eventData.date !== undefined && eventData.date !== null) {
      formData.append('date', eventData.date);
    }
    if (eventData.is_permanent !== undefined) {
      formData.append('is_permanent', eventData.is_permanent ? 'true' : 'false');
    }
    if (eventData.application_deadline !== undefined && eventData.application_deadline !== null) {
      formData.append('application_deadline', eventData.application_deadline);
    }
    if (eventData.location !== undefined) formData.append('location', eventData.location);
    if (eventData.capacity !== undefined) formData.append('capacity', eventData.capacity);
    if (eventData.fee !== undefined && eventData.fee !== null) {
      formData.append('fee', eventData.fee.toString());
    }
    if (eventData.category_id !== undefined && eventData.category_id !== null) {
      formData.append('category_id', eventData.category_id);
    }
    if (eventData.latitude !== undefined && eventData.latitude !== null) {
      formData.append('latitude', eventData.latitude);
    }
    if (eventData.longitude !== undefined && eventData.longitude !== null) {
      formData.append('longitude', eventData.longitude);
    }
    if (eventData.image && eventData.image instanceof File) {
      formData.append('image', eventData.image);
    }
    if (eventData.setup_time !== undefined) formData.append('setup_time', eventData.setup_time || '');
    if (eventData.power_availability !== undefined) formData.append('power_availability', eventData.power_availability || '');
    if (eventData.facilities !== undefined) formData.append('facilities', eventData.facilities || '');
    if (eventData.security_measures !== undefined) formData.append('security_measures', eventData.security_measures || '');
    if (eventData.insurance_requirements !== undefined) formData.append('insurance_requirements', eventData.insurance_requirements || '');
    if (eventData.safety_guidelines !== undefined) formData.append('safety_guidelines', eventData.safety_guidelines || '');
    if (eventData.required_documents !== undefined) formData.append('required_documents', eventData.required_documents || '');
    if (eventData.vendor_requirements !== undefined) formData.append('vendor_requirements', eventData.vendor_requirements || '');
    if (eventData.equipment_needed !== undefined) formData.append('equipment_needed', eventData.equipment_needed || '');
    if (eventData.additional_requirements !== undefined) formData.append('additional_requirements', eventData.additional_requirements || '');
    if (eventData.cancellation_policy !== undefined) formData.append('cancellation_policy', eventData.cancellation_policy || '');
    if (eventData.refund_policy !== undefined) formData.append('refund_policy', eventData.refund_policy || '');
    if (eventData.additional_notes !== undefined) formData.append('additional_notes', eventData.additional_notes || '');
    if (eventData.payment_instructions !== undefined) formData.append('payment_instructions', eventData.payment_instructions || '');
    if (eventData.payment_deadline_days !== undefined && eventData.payment_deadline_days !== null) {
      formData.append('payment_deadline_days', eventData.payment_deadline_days);
    }
    if (eventData.table_tiers !== undefined) {
      formData.append('table_tiers', typeof eventData.table_tiers === 'string' 
        ? eventData.table_tiers 
        : JSON.stringify(eventData.table_tiers || []));
    }
    if (eventData.custom_checkboxes !== undefined) {
      formData.append('custom_checkboxes', typeof eventData.custom_checkboxes === 'string' 
        ? eventData.custom_checkboxes 
        : JSON.stringify(eventData.custom_checkboxes || []));
    }
    if (eventData.custom_questions !== undefined) {
      formData.append('custom_questions', typeof eventData.custom_questions === 'string' 
        ? eventData.custom_questions 
        : JSON.stringify(eventData.custom_questions || []));
    }
    
    const response = await apiWithAuth(token).put(`/events/${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).delete(`/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

export const getOrganizerEvents = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get('/organizer/events');
    return response.data;
  } catch (error) {
    console.error('Error fetching organizer events:', error);
    throw error;
  }
};

export const getVendorEvents = async () => {
  const token = localStorage.getItem('token');
  const response = await apiWithAuth(token).get('/vendor/events');
  return response.data;
};

export const getEventApplications = async (eventId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get(`/events/${eventId}/applications/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching event applications:', error);
    throw error;
  }
};

export const updateApplicationStatus = async (applicationId, status) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).post(`/events/applications/${applicationId}/${status}`);
    return response.data;
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

export const confirmPayment = async (applicationId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).post(`/events/applications/${applicationId}/confirm-payment`);
    return response.data;
  } catch (error) {
    console.error('Error confirming payment:', error);
    throw error;
  }
};

export const updateApplicationAssignment = async (applicationId, tableId, setupId, selectedTierId) => {
  try {
    const token = localStorage.getItem('token');
    
    // Build request body - only include fields that are being updated
    const body = {};
    
    // Only include table_id if it's provided (even if null, to clear it)
    if (tableId !== undefined) {
      body.table_id = tableId !== null && tableId !== '' ? parseInt(tableId) : null;
    }
    
    // Only include setup_id if it's provided (even if null, to clear it)
    if (setupId !== undefined) {
      body.setup_id = setupId !== null && setupId !== '' ? parseInt(setupId) : null;
    }
    
    // Only include selected_tier_id if it's provided (even if null, to clear it)
    if (selectedTierId !== undefined) {
      body.selected_tier_id = selectedTierId !== null && selectedTierId !== '' ? parseInt(selectedTierId) : null;
    }
    
    const response = await apiWithAuth(token).put(`/applications/${applicationId}/assignment`, body);
    return response.data;
  } catch (error) {
    console.error('Error updating application assignment:', error);
    throw error;
  }
};

// Get a single event
export const getEvent = async (eventId) => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}/events/${eventId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const getOrganizerProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get('/organizer/profile');
    console.log('Organizer profile response:', response.data); // Debug log
    
    // Map the backend response to the frontend expected structure
    return {
      ...response.data,
      // Ensure name is properly mapped from organization_name
      name: response.data.organization_name || response.data.name || 'Organization Name',
      bio: response.data.contact_info || response.data.bio || 'No description available',
      email: response.data.contact_info || response.data.email, // Assuming contact_info contains email
      // Add default values for optional fields
      cover_photo_url: response.data.cover_photo_url || null,
      profile_picture_url: response.data.profile_picture_url || null,
      location: response.data.location || null,
      website: response.data.website || null,
      about: response.data.about || null,
      gallery: response.data.gallery || [],
      reviews: response.data.reviews || [],
    };
  } catch (error) {
    console.error('Error fetching organizer profile:', error);
    throw error;
  }
};

export const uploadGalleryImage = async (imageFile, caption) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', imageFile);
    if (caption) {
      formData.append('caption', caption);
    }
    
    const response = await apiWithAuth(token).post('/organizer/gallery/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Gallery upload response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error uploading gallery image:', error);
    throw error;
  }
};

export const deleteGalleryImage = async (imageId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).delete(`/organizer/gallery/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting gallery image:', error);
    throw error;
  }
};

export const updateOrganizerProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('token');
    const isFormData = profileData instanceof FormData;
    const response = await apiWithAuth(token).put('/organizer/profile', profileData, {
      headers: isFormData ? {
        'Content-Type': 'multipart/form-data',
      } : {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating organizer profile:', error);
    throw error;
  }
};

export const updateOrganizerSocialMedia = async (socialMediaData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).put('/organizer/social-media', socialMediaData);
    console.log('Social media update response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating social media:', error);
    throw error;
  }
};

export const getPublicOrganizerProfile = async (organizerId) => {
  try {
    const response = await apiWithAuth.get(`/organizers/${organizerId}/public`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPublicOrganizerEvents = async (organizerId) => {
  try {
    const response = await apiWithAuth.get(`/organizers/${organizerId}/events/public`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendMassEmail = async (eventId, emailData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).post(`/events/${eventId}/send-mass-email`, emailData);
    return response.data;
  } catch (error) {
    console.error('Error sending mass email:', error);
    throw error;
  }
};

// Vendor Profile Functions
export const getVendorProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get('/vendors/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching vendor profile:', error);
    throw error;
  }
};

export const getVendorAttendedEvents = async (vendorId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).get(`/vendors/${vendorId}/attended-events`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vendor attended events:', error);
    throw error;
  }
};

export const getPublicVendorProfile = async (vendorId) => {
  try {
    const response = await axios.get(`${API_URL}/vendors/${vendorId}/public`);
    return response.data;
  } catch (error) {
    console.error('Error fetching public vendor profile:', error);
    throw error;
  }
};

export const getPublicVendorEvents = async (vendorId) => {
  try {
    const response = await axios.get(`${API_URL}/vendors/${vendorId}/events/public`);
    return response.data;
  } catch (error) {
    console.error('Error fetching public vendor events:', error);
    throw error;
  }
};

export const updateVendorSocialMedia = async (socialMediaData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiWithAuth(token).put('/vendor/social-media', socialMediaData);
    return response.data;
  } catch (error) {
    console.error('Error updating vendor social media:', error);
    throw error;
  }
};
